'use client';

import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import StraightRulerIcon from '@mui/icons-material/Straighten';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Box, IconButton, Paper, Stack, Tooltip } from '@mui/material';
import { OrbitControls, Html } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { Suspense, useCallback } from 'react';
import { Scene } from './Scene';
import { STLViewerProps } from './types';
import { LoadingSpinner, CanvasLoadingSpinner } from './LoadingSpinner';
import { useViewer } from './hooks/useViewer';
import { useChat3DStore } from 'stores/chat3DStore';
import './styles.css';

function ViewerControls({
  loading,
  onCaptureViews,
}: {
  loading: boolean;
  onCaptureViews?: () => Promise<void>;
}) {
  const { measureMode, measurePoints, toggleMeasureMode, clearMeasurements, renderCode, code } =
    useChat3DStore();

  const handleRender = () => {
    renderCode(code);
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        background: 'rgba(255,255,255,0.8)',
        borderRadius: 1,
        padding: '4px',
      }}
    >
      <Tooltip title="Render">
        <IconButton
          size="small"
          onClick={handleRender}
          disabled={loading}
          sx={{ bgcolor: 'background.paper' }}
        >
          <PlayArrowIcon />
        </IconButton>
      </Tooltip>

      <Tooltip title={measureMode ? 'Exit Measure Mode' : 'Measure Distance'}>
        <IconButton
          size="small"
          onClick={toggleMeasureMode}
          color={measureMode ? 'primary' : 'default'}
          disabled={loading}
        >
          {loading ? <LoadingSpinner size={20} /> : <StraightRulerIcon />}
        </IconButton>
      </Tooltip>

      {measurePoints.length > 0 && (
        <Tooltip title="Clear Measurements">
          <IconButton size="small" onClick={clearMeasurements}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      )}

      {onCaptureViews && (
        <Tooltip title="Capture Views">
          <IconButton size="small" onClick={onCaptureViews}>
            <PhotoCameraIcon />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

function CanvasContent({
  url,
  showAxes,
  onCaptureViews,
}: {
  url: string;
  showAxes: boolean;
  onCaptureViews?: (views: string[]) => Promise<void>;
}) {
  const { handleClick, isLoading, captureView } = useViewer(url);
  const { measureMode, measurePoints } = useChat3DStore();

  const handleCaptureViews = useCallback(async () => {
    if (!onCaptureViews) return;
    const view = captureView();
    if (view) {
      await onCaptureViews([view]);
    }
  }, [captureView, onCaptureViews]);

  return (
    <>
      <Suspense fallback={<CanvasLoadingSpinner />}>
        {url && (
          <Scene
            modelDataUrl={url}
            showAxes={showAxes}
            handleClick={handleClick}
            isLoading={isLoading}
            measureMode={measureMode}
            measurePoints={measurePoints}
          />
        )}
        <OrbitControls
          makeDefault
          minPolarAngle={0}
          maxPolarAngle={Math.PI}
          enableDamping={false}
          enableZoom
          enablePan
        />
        {onCaptureViews && (
          <Html wrapperClass="canvas-controls-wrapper" className="canvas-controls-inner">
            <ViewerControls loading={isLoading} onCaptureViews={handleCaptureViews} />
          </Html>
        )}
      </Suspense>
    </>
  );
}

const canvasSx = {
  position: 'relative' as const,
  overflow: 'hidden',
  '& canvas': {
    outline: 'none',
  },
};

/** STL viewer canvas only (no Paper/chrome). Use when embedding inside your own card/container. */
export function STLViewerCanvas({
  url,
  width = '100%',
  height = 400,
  onCaptureViews,
  showAxes = false,
}: STLViewerProps) {
  return (
    <Box
      sx={{
        width,
        height,
        ...canvasSx,
      }}
    >
      <Canvas
        camera={{ position: [0, 0, 5], fov: 50 }}
        style={{ background: '#f5f5f5' }}
        dpr={[1, 2]}
        performance={{
          min: 0.5,
        }}
        onError={error => {
          console.error('Canvas error:', error);
        }}
      >
        {url && <CanvasContent url={url} showAxes={showAxes} onCaptureViews={onCaptureViews} />}
      </Canvas>
    </Box>
  );
}

function STLViewerComponent(props: STLViewerProps) {
  const { width = '100%', height = 400 } = props;
  return (
    <Box component={Paper} elevation={2} sx={{ width, height, ...canvasSx }}>
      <STLViewerCanvas {...props} />
    </Box>
  );
}

export default STLViewerComponent;
